/**
 * Session-scoped persistence for the TanStack Query cache.
 *
 * The problem this solves: the query cache lives in memory, so a hard refresh
 * (F5) throws it away and every board reloads from nothing — a full skeleton
 * for bytes the backend has not rebuilt since the last poll. `gcTime` cannot
 * help, because the page it belonged to no longer exists.
 *
 * Why persisting is defensible here: NOVA is an internal IT/security dashboard
 * behind Entra SSO, so a signed-in user is already entitled to everything the
 * boards show them. What is *not* defensible is leaving that estate on disk
 * after the session ends — a laptop left in a taxi should not carry yesterday's
 * infrastructure and licence data.
 *
 * Hence **`sessionStorage`, never `localStorage`**: it survives F5, in-app
 * navigation and back/forward within the tab (the actual complaint), and the
 * browser discards it when the tab closes. Nothing is written to a profile
 * directory that outlives the session.
 *
 * Three further guards, in order of what they defend against:
 *
 *   1. `shouldDehydrateQuery` — an **allow-list**, so a query is written only
 *      if it is named below. See `PERSISTABLE_KEYS`; personal data never
 *      reaches storage at all.
 *   2. `maxAge` — browsers restore `sessionStorage` when they restore a
 *      crashed or "continue where you left off" session, which can resurrect a
 *      tab from yesterday. `maxAge` bounds how old a rehydrated payload may be.
 *   3. `buster` — keyed to the build, so a deploy that changes a payload shape
 *      can never hydrate the old shape into the new components.
 *
 * Writes fail soft. `sessionStorage` can be absent (blocked storage, hardened
 * profiles) or full (`QuotaExceededError`); neither may take the dashboard
 * down. See `resolveSessionStorage` and the `retry` option.
 */

import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import {
  defaultShouldDehydrateQuery,
  type OmitKeyof,
  type Query,
  type QueryKey,
} from "@tanstack/react-query";
import {
  type PersistQueryClientOptions,
  removeOldestQuery,
} from "@tanstack/react-query-persist-client";

import { logger } from "@/lib/logger";

/** Storage slot. Namespaced so it can't collide with anything else on the origin. */
const STORAGE_KEY = "nova-query-cache";

/**
 * How old a restored cache may be, in ms.
 *
 * Matched to the client's `gcTime` (2 h) on purpose: rehydration should never
 * revive a payload the in-memory cache would itself have dropped. It exists for
 * the session-restore case — the browser reopening yesterday's tabs — because a
 * tab that simply stays open keeps its cache in memory anyway.
 */
const MAX_AGE_MS = 2 * 60 * 60 * 1000;

/**
 * Cache-invalidation key tied to this build.
 *
 * `vite.config.ts` defines `VITE_BUILD_ID` as `<version>+<build timestamp>`, so
 * every `vite build` produces a new value and a deploy can never hydrate a
 * persisted payload whose shape changed with it. Falls back to a constant if
 * the define is missing, which only happens outside a Vite build (e.g. a test
 * runner) — persistence is inert there anyway.
 */
const BUILD_ID: string =
  typeof import.meta.env.VITE_BUILD_ID === "string" && import.meta.env.VITE_BUILD_ID
    ? import.meta.env.VITE_BUILD_ID
    : "dev";

/**
 * The only query keys written to storage — matched as prefixes, so
 * `["monitoring", "history"]` covers `["monitoring", "history", 90]`.
 *
 * **This is an allow-list, and deliberately leaf-level.** A deny-list would
 * fail open: the next hook someone adds would be persisted by default, and the
 * person adding it is not necessarily thinking about disk. Deny-by-default
 * means a new query is simply not persisted until someone reads this comment.
 *
 * Every entry below was checked against its response type in
 * `src/lib/api/generated/types.gen.ts` and carries aggregates or
 * infrastructure identifiers only — sites, subscriptions, resources, SKUs,
 * counts, day series. No person is named in any of them.
 */
const PERSISTABLE_KEYS: ReadonlyArray<ReadonlyArray<string>> = [
  // Websites: SiteStatus — site name, URL, status, latency. Infrastructure.
  ["monitoring", "overview"],
  ["monitoring", "history"],
  // Network (Cato): NetworkSite / NetworkEvent — site, PoP, link, device. No user.
  ["network", "overview"],
  ["network", "sites"],
  ["network", "links"],
  // NOTE the omission of ["security", …] — see NEVER_PERSIST_KEYS. It was
  // persistable while the board was aggregates-only; the 2026-08-20 decision to
  // show named Very Attacked People changed the payload, and with it the answer.
  // M365 service health: service and incident status. No tenant user data.
  ["m365", "service-health"],
  ["m365", "incidents"],
  // Cloud: estate, workloads, resource health, alerts, advisor, cost,
  // governance. Resource and subscription identifiers only.
  // NOTE the omission of ["cloud", "changes"] — see NEVER_PERSIST_KEYS.
  ["cloud", "overview"],
  ["cloud", "workloads"],
  ["cloud", "service-health"],
  ["cloud", "advisor"],
  ["cloud", "alerts"],
  ["cloud", "cost"],
  ["cloud", "governance"],
  // Dynamics 365 F&O: checked field by field against `FoOverview`,
  // `FoUserExperience`, `FoRequests` and `FoIntegration`. Every row is an
  // aggregate over an *entity* — an environment, a form name, a request name, a
  // result code, a dependency target, a Service Bus queue — never over a person.
  // `distinct_users` is a count, not an identity, and cohorts below the k≥5
  // floor lose their label entirely. Two things the backend deliberately never
  // puts in these payloads
  // and so cannot leak here: a user identifier (the source report's
  // `TopUsersPain` page has no equivalent route) and SQL statement text, which
  // would embed the literal data values a statement ran against.
  // Worth persisting for the same reason as Cloud: these snapshots are rebuilt
  // on 2-15 min backend TTLs, so an F5 that refetched them would show a
  // skeleton for bytes that had not changed.
  ["fo", "overview"],
  ["fo", "user-experience"],
  ["fo", "requests"],
  ["fo", "integration"],
  // F&O Performance Console aggregates: classification shares, worst forms and
  // sites, collector status. Counts and form names only. NOTE the omission of
  // every other ["fo", "console", …] key — see `NEVER_PERSIST_KEYS` below.
  ["fo", "console", "slow-summary"],
  ["fo", "console", "collect-status"],
  // Browser-RUM rollout status: users/records in 24 h as counts, a coverage
  // ratio and a timestamp. No poster identity is in the payload.
  ["fo", "console", "rum-status"],
  // Browser-RUM experience panel: per-app medians, hourly TTFB, k-folded
  // contexts and probe timings. Aggregates only, no poster identity.
  ["fo", "console", "experience"],
  // Licences: SKU-level seat counts and waste. Aggregate rows, no accounts.
  // NOTE the omission of ["licenses", "reclaimable-accounts"] — see below.
  ["licenses", "overview"],
  ["licenses", "skus"],
  ["licenses", "waste"],
];

/**
 * Keys that must never be persisted, checked before the allow-list.
 *
 * Redundant against `PERSISTABLE_KEYS` today — none of these is listed there —
 * and that is the point: it is the guard for the day someone widens an entry to
 * a whole board (`["licenses"]`) without re-reading the payload. Each one
 * carries data about a *named person*:
 *
 *   - `["licenses", "reclaimable-accounts"]` — `ReclaimableAccount` carries
 *     `display_name` and `user_principal_name` for real employees. Its hook
 *     already runs a 2 min `gcTime` against the client's 2 h precisely so the
 *     identities do not linger in memory; writing them to storage would undo
 *     that and outlive the drawer they were opened in.
 *   - `["cloud", "changes"]` — `CloudChange.changed_by` is documented as "a
 *     UPN, app ID, or Azure service". A UPN is an identified employee, so the
 *     change feed is personal data even though it reads as infrastructure.
 *     This is the board that looks safe and isn't.
 *   - `["security", …]` — the Security board used to be
 *     aggregates by construction, and was persisted on exactly that basis. Timo's
 *     2026-08-20 decision put **named** Very Attacked People on it: the security
 *     team cannot protect a targeted individual without knowing who they are.
 *     The names are role-gated by the backend and belong on the board, but not
 *     in a browser storage slot that outlives the panel they were read in — a
 *     list of the employees an attacker is concentrating on is precisely the
 *     thing not to leave lying around. Refetching costs TAP nothing (both routes
 *     are served from stored rollups), so there is no reason to keep them.
 *   - `["me"]` — the signed-in user's own name and email. Cheap to refetch, and
 *     it must be re-answered by the backend against the session cookie on every
 *     load rather than trusted from storage: it is what `ProtectedRoute` and
 *     `AdminRoute` read.
 *   - `["fo", "console", "user" | "record" | "search" | "audit" | "device"]` —
 *     the F&O
 *     Performance Console's per-user layer (concept §11, D7). A person's
 *     timeline, one of their slow-interaction records, the directory search
 *     candidates, the machines Defender has seen them on and the
 *     who-looked-at-whom audit list are each a named
 *     employee's data, served on demand against an audit-logged reason. They
 *     must not outlive the investigation in a storage slot the reason never
 *     covered; the hooks already keep them on a 2 min `gcTime` for the same
 *     reason. Only the console's aggregates (`slow-summary`, `collect-status`)
 *     are in the allow-list above.
 */
const NEVER_PERSIST_KEYS: ReadonlyArray<ReadonlyArray<string>> = [
  ["licenses", "reclaimable-accounts"],
  ["cloud", "changes"],
  ["security"],
  ["me"],
  ["fo", "console", "user"],
  ["fo", "console", "record"],
  ["fo", "console", "search"],
  ["fo", "console", "audit"],
  // User-submitted slowness reports: every row carries the submitter's mail
  // and name (their own data, consented by the click — but still a named
  // person's), and the detail is reason-gated and audit-logged like a record.
  ["fo", "console", "reports"],
  ["fo", "console", "report"],
  // Device facts for one person: the machines Defender has seen them signed in
  // on, by name, model and OS build. An asset list tied to an individual is as
  // personal as their timeline — arguably more portable, since a device name is
  // a thing an attacker can use — and it is read against an audit-logged reason
  // that a storage slot would outlive. The hook already keeps it on the same
  // 2 min `gcTime`.
  ["fo", "console", "device"],
];

/** True when `key` starts with every segment of `prefix`. */
function matchesPrefix(key: QueryKey, prefix: ReadonlyArray<string>): boolean {
  return prefix.length <= key.length && prefix.every((segment, index) => key[index] === segment);
}

/**
 * The persistence decision for one query key, exported so it can be reasoned
 * about (and asserted on) without standing up a query client.
 */
export function isPersistableQueryKey(key: QueryKey): boolean {
  if (NEVER_PERSIST_KEYS.some((prefix) => matchesPrefix(key, prefix))) {
    return false;
  }
  return PERSISTABLE_KEYS.some((prefix) => matchesPrefix(key, prefix));
}

/**
 * Resolve `sessionStorage`, or `undefined` if it cannot be used.
 *
 * Reading `window.sessionStorage` throws outright when storage is blocked by
 * policy or a hardened profile, and some environments hand back an object that
 * throws on first write — so probe it. Returning `undefined` makes the persister
 * a documented no-op: the dashboard runs exactly as it did before persistence,
 * just without surviving F5.
 */
function resolveSessionStorage(): Storage | undefined {
  try {
    const storage = window.sessionStorage;
    const probeKey = `${STORAGE_KEY}-probe`;
    storage.setItem(probeKey, "1");
    storage.removeItem(probeKey);
    return storage;
  } catch (error) {
    logger.warn("[persist] sessionStorage unavailable — cache will not survive a refresh", error);
    return undefined;
  }
}

const persister = createSyncStoragePersister({
  storage: resolveSessionStorage(),
  key: STORAGE_KEY,
  // Coalesce the write burst a board's seven parallel queries would otherwise
  // cause: every cache event triggers a save, and each save serialises the
  // whole client.
  throttleTime: 1_000,
  // Quota handling. A write that throws (`QuotaExceededError` — the origin's
  // ~5 MB session budget, shared with anything else on it) is caught by the
  // persister, which asks this retryer for a smaller client and tries again;
  // `removeOldestQuery` drops the least-recently-updated query each round and
  // gives up when there is nothing left to drop. Either way the exception never
  // escapes: the worst outcome is a smaller (or skipped) persisted cache, and
  // the in-memory cache is untouched.
  retry: removeOldestQuery,
});

/**
 * Options for `<PersistQueryClientProvider>`. The provider supplies
 * `queryClient` itself, hence the omission.
 */
export const persistOptions: OmitKeyof<PersistQueryClientOptions, "queryClient"> = {
  persister,
  maxAge: MAX_AGE_MS,
  buster: BUILD_ID,
  dehydrateOptions: {
    // Compose with the library default rather than replacing it: the default
    // is what keeps pending and errored queries out of storage, so a failed
    // request is never persisted as though it were an answer.
    shouldDehydrateQuery: (query: Query) =>
      defaultShouldDehydrateQuery(query) && isPersistableQueryKey(query.queryKey),
    // Nothing here benefits from resuming a mutation across a reload: `logout`
    // is idempotent and `refreshMonitoring` would re-probe the estate on a
    // refresh the user did not ask for. (The default only persists *paused*
    // mutations, so this mostly documents the intent.)
    shouldDehydrateMutation: () => false,
  },
};

/** Restore succeeded — the boards paint from the previous page's cache. */
export function onPersistRestored(): void {
  logger.debug("[persist] query cache restored from sessionStorage");
}

/**
 * Restore failed and the persisted cache was discarded by the library.
 *
 * Not a user-facing failure: the app simply starts cold, which is the behaviour
 * it had before persistence existed.
 */
export function onPersistError(): void {
  logger.warn("[persist] could not restore query cache — starting from an empty cache");
}

/**
 * Delete the persisted cache immediately.
 *
 * Called on sign-out. `queryClient.clear()` alone would eventually overwrite
 * storage with an empty snapshot, but only after the persister's throttle
 * window — removing it outright means the moment the session ends, the estate
 * is gone from the tab.
 */
export function clearPersistedQueryCache(): void {
  try {
    void persister.removeClient();
  } catch (error) {
    // Never let a storage failure break sign-out; the data dies with the tab.
    logger.warn("[persist] could not clear the persisted query cache", error);
  }
}
